import { GraphQLError } from "graphql";
import {
    clearLocalStorage,
    exchangeRefreshTokenRequest,
} from "../shared/services/auth/auth.service";

export interface AHAGraphQLError extends GraphQLError {
    details: string[];
    internalCode: number;
}

/**
 * The API's exact message for an expired token. If the server renames it, the refresh path below
 * never runs; the test that pins this string is what would catch that.
 */
export const UNAUTHORIZED_MESSAGE = "Unathorized graphql request";

/**
 * When the API rejects the access token: exchange the refresh token for a new one, or end the
 * session locally if that fails. exchangeRefreshTokenRequest returns { errors } instead of
 * rejecting, so the missing token is checked explicitly; writing back accessToken: undefined
 * would reload into a loop.
 */
// Typed by what it reads: only message is needed, and Apollo's error type and the app's own are
// not assignable to each other.
export const handleAuthError = async (
    graphQLErrors: readonly { message: string }[] | undefined
): Promise<void> => {
    if (!graphQLErrors || graphQLErrors[0] === null) return;

    for (const { message } of graphQLErrors) {
        if (message !== UNAUTHORIZED_MESSAGE) continue;

        const token = JSON.parse(localStorage.getItem("token") || "{}");

        if (!token || !token.accessToken) {
            // No session to refresh: clear what is left, without reloading.
            clearLocalStorage();
            continue;
        }

        try {
            const resp: any = await exchangeRefreshTokenRequest(
                token.refreshToken
            );

            // Explicit, rather than relying on `resp.response.accessToken` throwing.
            if (resp?.errors || !resp?.response?.accessToken) {
                throw new Error(
                    "refresh token exchange returned no access token"
                );
            }

            token.accessToken = resp.response.accessToken;
            localStorage.setItem("token", JSON.stringify(token));
            window.location.reload();
        } catch {
            clearLocalStorage();
            window.location.reload();
        }
    }
};
